/* eslint-disable no-plusplus */
/* eslint-disable max-nested-callbacks */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState, useCallback } from 'react';

export const useCalculateBitrates = (
  videoDeviceId: string,
  audioDeviceId: string,
) => {
  const [videoBitrates, setVideoBitrates] = useState<number[]>([]);
  const [audioBitrates, setAudioBitrates] = useState<number[]>([]);

  const calculateBitrate = (
    sizeInBytes: number,
    durationInSeconds: number,
  ): number => {
    return (sizeInBytes * 8) / durationInSeconds / 1000; // bits per second (bps)
  };

  const calculateAverage = (bitrateArray: number[]) => {
    if (bitrateArray.length === 0) {
      return 0;
    }
    const sum = bitrateArray.reduce((a, b) => a + b, 0);
    return sum / bitrateArray.length;
  };

  const testBitrate = useCallback(
    async (
      deviceId: string,
      kind: 'audio' | 'video',
      updateBitrates: (bitrate: number[]) => void,
    ): Promise<void> => {
      const constraints =
        kind === 'audio'
          ? { audio: { deviceId: { exact: deviceId } }, video: false }
          : { audio: false, video: { deviceId: { exact: deviceId } } };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      return new Promise<void>((resolve, reject) => {
        try {
          const mimeType = kind === 'audio' ? 'audio/webm' : 'video/webm';
          const fallbackMimeType = kind === 'audio' ? 'audio/mp4' : 'video/mp4'; // Fallback MIME types for Safari

          const supportedMimeType = MediaRecorder.isTypeSupported(mimeType)
            ? mimeType
            : fallbackMimeType;

          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: supportedMimeType,
          });
          const bitrates: number[] = [];
          let chunks: Blob[] = [];
          let count = 0;

          mediaRecorder.ondataavailable = event => {
            chunks.push(event.data);
          };

          mediaRecorder.onstop = () => {
            const blob = new Blob(chunks);
            const sizeInBytes = blob.size;
            const bitrate = calculateBitrate(sizeInBytes, 0.5);
            bitrates.push(bitrate);
            chunks = [];
            updateBitrates(bitrates);

            count++;
            if (count < 20) {
              mediaRecorder.start();
              setTimeout(() => mediaRecorder.stop(), 500);
            } else {
              resolve();
              stream.getTracks().forEach(track => track.stop()); // Stop all tracks
            }
          };

          mediaRecorder.start();
          setTimeout(() => mediaRecorder.stop(), 500);
        } catch (error) {
          reject(error);
        }
      });
    },
    [],
  );

  const startBitrateTest = useCallback(async () => {
    setVideoBitrates([]);
    setAudioBitrates([]);

    const p1 = testBitrate(videoDeviceId, 'video', bitrates =>
      setVideoBitrates([...bitrates]),
    );
    const p2 = testBitrate(audioDeviceId, 'audio', bitrates =>
      setAudioBitrates([...bitrates]),
    );

    await Promise.all([p1, p2]);
  }, [testBitrate, videoDeviceId, audioDeviceId]);

  return {
    startBitrateTest,
    videoBitrates,
    audioBitrates,
    audioBitratesAverage: calculateAverage(audioBitrates),
    videoBitratesAverage: calculateAverage(videoBitrates),
  };
};
